import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Menu, MenuListItem } from '@ricettario/shared';
import { api, ApiError } from '../api/client';
import { useMenus } from '../hooks/useMenus';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
  });
}

export default function MenuListPage() {
  const { data, loading, error, refresh } = useMenus();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  async function createMenu() {
    if (creating) return;
    setCreating(true);
    try {
      const menu = await api.post<Menu>('/api/v1/menus', {});
      navigate(`/menu/${menu.id}`);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore creazione');
    } finally {
      setCreating(false);
    }
  }

  const menus = data ?? [];

  return (
    <main className="max-w-xl mx-auto px-5 pt-8 pb-32">
      <nav className="mb-4">
        <Link to="/" className="btn-ghost">← ricette</Link>
      </nav>

      <header className="mb-7 animate-riseIn">
        <div className="text-sm text-muted font-medium">Menu settimanale 🛒</div>
        <h1 className="mt-1 font-extrabold tracking-tightest leading-none text-[clamp(24px,7vw,32px)]">
          Pianifica la <span className="text-brand">settimana</span>
        </h1>
        <p className="text-sm text-muted mt-2 leading-relaxed">
          Scegli le ricette, somma gli ingredienti, manda la spesa a Bring in un colpo solo.
        </p>
      </header>

      {loading && <ListSkeleton />}
      {error && <p className="text-brand font-medium text-center mt-10">{error}</p>}
      {!loading && !error && menus.length === 0 && <EmptyState onCreate={createMenu} creating={creating} />}

      <div className="flex flex-col gap-3">
        {menus.map((m, idx) => (
          <MenuCard key={m.id} menu={m} delay={idx * 40} onDelete={refresh} />
        ))}
      </div>

      {menus.length > 0 && (
        <button
          onClick={createMenu}
          disabled={creating}
          className="btn-brand fixed bottom-5 right-5 h-14 pr-6 pl-5 no-tap"
          aria-label="nuovo menu"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            className="h-5 w-5"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span>{creating ? '…' : 'Nuovo'}</span>
        </button>
      )}
    </main>
  );
}

function MenuCard({
  menu,
  delay,
  onDelete,
}: {
  menu: MenuListItem;
  delay: number;
  onDelete: () => void;
}) {
  const [deleting, setDeleting] = useState(false);

  async function remove(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Eliminare questo menu?')) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v1/menus/${menu.id}`);
      onDelete();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore eliminazione');
      setDeleting(false);
    }
  }

  const title = menu.name?.trim() || `Menu del ${formatDate(menu.createdAt)}`;

  return (
    <Link
      to={`/menu/${menu.id}`}
      className="card p-4 flex items-center gap-4 no-tap active:scale-[0.99] transition animate-riseIn"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className="h-14 w-14 rounded-2xl flex items-center justify-center text-2xl shrink-0"
        style={{ background: 'linear-gradient(135deg,#FFD4C2,#F3856F)' }}
        aria-hidden
      >
        🛒
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="font-bold text-[15px] leading-[1.2] tracking-tightest truncate">{title}</h2>
        <div className="flex items-center gap-2 text-xs text-muted font-medium mt-1 flex-wrap">
          <span>
            {menu.itemsCount} ricett{menu.itemsCount === 1 ? 'a' : 'e'}
          </span>
          {menu.sentAt && (
            <span className="chip chip-sage text-[10.5px] px-2 py-0.5">
              inviato {formatDate(menu.sentAt)}
            </span>
          )}
          {menu.recipePreviews.length > 0 && (
            <span className="text-muted truncate">
              · {menu.recipePreviews.map((r) => r.icon ?? '🍽️').join(' ')}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={remove}
        disabled={deleting}
        className="btn-ghost text-xs shrink-0 !p-2"
        aria-label="elimina menu"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
        </svg>
      </button>
    </Link>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card p-4 flex items-center gap-4">
          <div className="skeleton h-14 w-14 rounded-2xl" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-1/2" />
            <div className="skeleton h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onCreate, creating }: { onCreate: () => void; creating: boolean }) {
  return (
    <div className="card py-14 text-center mt-4 animate-riseIn">
      <div
        className="mx-auto mb-5 h-20 w-20 rounded-3xl flex items-center justify-center text-5xl"
        style={{ background: 'linear-gradient(135deg,#FFD4C2,#F3856F)' }}
        aria-hidden
      >
        🛒
      </div>
      <p className="text-lg font-bold text-ink mb-1">Ancora nessun menu.</p>
      <p className="text-sm text-muted px-10 mb-6">Crea il primo, aggiungi 3-5 ricette e fai la spesa in un clic.</p>
      <button className="btn-brand" onClick={onCreate} disabled={creating}>
        {creating ? 'attendi…' : 'Crea il primo menu'}
      </button>
    </div>
  );
}
